import { CalendarRange, UserPlus, Zap } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { DomainCard, Eyebrow, IconTile, type Domain } from "@appcaire/ui";
import { brandHomeCopy } from "./brandHomeCopy";
import type { LandingLocale } from "../landingCopy";
import {
  homeSectionClass,
  homeSectionHeadClass,
  homeSectionTitleClass,
} from "./HomeParts";

/** Flow → domain is fixed: the client (human), planning, the day's attention. */
const FLOWS: { domain: Domain; icon: LucideIcon }[] = [
  { domain: "human", icon: UserPlus },
  { domain: "planning", icon: CalendarRange },
  { domain: "attention", icon: Zap },
];

/** The three planning flows, all built on the same care plans. */
export function ThreeFlowsSection({ locale }: { locale: LandingLocale }) {
  const c = brandHomeCopy[locale].flows;
  return (
    <section
      className={homeSectionClass}
      aria-labelledby="three-flows-heading"
      data-home-section="flows"
    >
      <div className={homeSectionHeadClass}>
        <div>
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h2 id="three-flows-heading" className={homeSectionTitleClass}>
            {c.title}
          </h2>
        </div>
        <p>{c.lead}</p>
      </div>
      <ol className="grid gap-4 lg:grid-cols-3" role="list">
        {c.items.map((flow, i) => (
          <li key={flow.title} className="flex">
            <DomainCard domain={FLOWS[i].domain} className="h-full">
              <div className="flex items-center gap-3">
                <IconTile
                  icon={FLOWS[i].icon}
                  domain={FLOWS[i].domain}
                  size="s"
                />
                <Eyebrow tone={FLOWS[i].domain} as="span">
                  {`${i + 1} · ${flow.label}`}
                </Eyebrow>
              </div>
              <h3 className="text-[21px] font-extrabold leading-tight text-caire-ink">
                {flow.title}
              </h3>
              <p className="text-[15px] leading-normal text-caire-ink/85">
                {flow.text}
              </p>
            </DomainCard>
          </li>
        ))}
      </ol>
      <p className="mt-6 max-w-[70ch] text-[17px]">{c.channels}</p>
    </section>
  );
}
